/**
 * abilityPalette — read an ability's authored color palette
 * (VFX-evolution M1.S6). The semantic colors ("combustion is fire-orange")
 * live in each ability's `visual` table in h2kdb TOML, carried into
 * abilities.gen by compile-content. VFX effects resolve a canonical color
 * here instead of re-typing hex.
 *
 * The RECIPE (which primitive, timings, glow routing) stays local in the
 * effect defs; only the COLOR identity comes from content.
 */
import { getAbilityData } from '../../shared/content/generated/abilities.gen';

export interface AbilityPalette {
  /** color_palette[] as 0xRRGGBB numbers (primary → accents). */
  palette: number[];
  /** sprite_hints.glow_color as 0xRRGGBB, or the first palette entry. */
  glowColor?: number;
}

/** '#rrggbb' | 'rrggbb' → 0xRRGGBB number, or null if unparseable. */
export function hexToNumber(hex: string): number | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  return m ? parseInt(m[1], 16) : null;
}

/** Authored palette for an ability, or null when its TOML declares none. */
export function abilityPalette(abilityId: string): AbilityPalette | null {
  const visual = getAbilityData(abilityId)?.visual;
  if (!visual) return null;
  const palette = (visual.palette ?? [])
    .map(hexToNumber)
    .filter((n): n is number => n != null);
  const glowColor = visual.glowColor ? hexToNumber(visual.glowColor) ?? undefined : undefined;
  if (!palette.length && glowColor === undefined) return null;
  return { palette, glowColor: glowColor ?? palette[0] };
}

/**
 * The single canonical color to tint an ability's VFX with — glow color
 * first, then the palette primary. Pass as the `color` override when
 * playing an effect for a specific ability so it tints to the authored color.
 * Returns undefined when the ability declares no visual (effect keeps its own).
 */
export function abilityVfxColor(abilityId: string): number | undefined {
  const p = abilityPalette(abilityId);
  return p?.glowColor ?? p?.palette[0];
}
